import Media from 'react-media';
import { useTracking } from 'react-tracking';

import {
  Box,
  Button as LegacyButton,
  Grid,
  Text,
  useTheme,
} from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import Carousel from '@components/Carousel';
import { Sentry } from '@/components/Sentry';

import { useCompanyNarratives } from '@/pages/CompanyReport/components/CompanyNarrativesDeprecated/hooks/useCompanyNarratives';
import { Section } from '@/pages/CompanyReport/components/Overview/components/Section';
import { SubSectionHeader } from '@/pages/CompanyReport/components/Overview/components/SubSectionHeader';

import { StyledHeaderGrid } from './styled';
import { StyledEmbla } from '@/components/Carousel/styled';
import { NarrativeCardPopulated } from '@/components/NarrativeCard';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { ROUTE_PUBLIC_NARRATIVE_CREATE } from '@/constants/routes';
import { getLocationPathname } from '@/redux/location/selectors';
import { useSelector } from 'react-redux';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';
import { NarrativeInvalidationBoundary } from '@/pages/Narrative/components/NarrativeInvalidationBoundary';

const VISIBLE_NARRATIVES = 4;

export const CompanyNarratives = ({
  canonicalUrl,
}: {
  canonicalUrl: string;
}) => {
  const theme = useTheme();
  const { isMobile, isDesktop } = useUserAgent();
  const { data, status } = useCompanyNarratives({ canonicalUrl });
  const narratives = data?.NarrativesByCompanyCanonical;
  const pathname = useSelector(getLocationPathname);
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
  });

  if (status === 'error' || status === 'pending') {
    return null;
  }

  if (typeof narratives === 'undefined' || narratives.length === 0) {
    return null;
  }

  const handleOnClick = (narrative: (typeof narratives)[number]) => {
    const { id, company } = narrative;
    return trackEvent({
      action: 'click',
      modifier: 'view',
      narrativeId: id,
      companyId: company?.id,
      uniqueSymbol: company?.tickerSymbol,
    });
  };

  const hasMoreThanOneNarrative = narratives.length > 1;
  const hasOneNarrative = narratives.length === 1;
  const companyId = narratives?.[0]?.company?.id;

  return (
    <Track>
      <Section data-cy-id="company-narratives">
        <StyledHeaderGrid
          as="header"
          templateColumns="1fr max-content"
          gap="8px"
        >
          <SubSectionHeader data-cy-id="company-narratives-heading">
            Community Narratives
          </SubSectionHeader>
          {companyId && (
            <LegacyButton
              as={RouterLink}
              styleType="loud"
              to={{
                type: ROUTE_PUBLIC_NARRATIVE_CREATE,
                payload: {
                  query: { company: companyId, returnPath: pathname },
                },
              }}
              alignIcon="left"
            >
              <PlusSolid />
              Create a narrative
            </LegacyButton>
          )}
        </StyledHeaderGrid>
        <Box marginBottom="x2">
          <Text typography="xSmall" data-cy-id="company-narratives-by-line">
            Narratives bring a range of perspectives from our community.
          </Text>
        </Box>
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { canonicalUrl } }}
          globalDisabled
        >
          <NarrativeInvalidationBoundary
            onSetPrimary={({ invalidatePriority }) =>
              invalidatePriority(['narrative-company-report-list'])
            }
          >
            <Media
              queries={{
                mobile: { maxWidth: theme.smMaxRaw },
                desktop: { minWidth: theme.lgMinRaw },
              }}
              defaultMatches={{
                mobile: isMobile,
                desktop: isDesktop,
              }}
            >
              {(matches) => {
                if (matches.mobile) {
                  return (
                    <StyledEmbla>
                      <Carousel
                        align="center"
                        containScroll="trimSnaps"
                        showArrows={hasMoreThanOneNarrative}
                        showGradient={hasMoreThanOneNarrative}
                        totalSlides={VISIBLE_NARRATIVES}
                      >
                        {hasOneNarrative ? (
                          <Grid width="100%">
                            <div>
                              {narratives.map((narrative) => (
                                <NarrativeCardPopulated
                                  key={narrative.id}
                                  narrative={narrative}
                                  onClick={() => handleOnClick(narrative)}
                                />
                              ))}
                            </div>
                          </Grid>
                        ) : (
                          <Grid
                            gap="8px"
                            templateColumns={`repeat(${VISIBLE_NARRATIVES}, 317px)`}
                            width="100%"
                          >
                            {narratives
                              .slice(0, VISIBLE_NARRATIVES)
                              .map((narrative) => (
                                <div key={narrative.id}>
                                  <NarrativeCardPopulated
                                    narrative={narrative}
                                    onClick={() => handleOnClick(narrative)}
                                  />
                                </div>
                              ))}
                          </Grid>
                        )}
                      </Carousel>
                    </StyledEmbla>
                  );
                }

                return (
                  <Grid gap={`${theme.x2}px`} templateColumns="repeat(2, 1fr)">
                    {narratives
                      .slice(0, VISIBLE_NARRATIVES)
                      .map((narrative) => (
                        <NarrativeCardPopulated
                          key={narrative.id}
                          narrative={narrative}
                          onClick={() => handleOnClick(narrative)}
                        />
                      ))}
                  </Grid>
                );
              }}
            </Media>
            {narratives.length > VISIBLE_NARRATIVES && (
              <div className="mt-x2 grid grid-cols-1">
                <LegacyButton
                  styleType="loudLarge"
                  as={RouterLink}
                  to={canonicalUrl.replace('/stocks', '/community/narratives')}
                  className="text-center"
                >
                  View all narratives
                </LegacyButton>
              </div>
            )}
          </NarrativeInvalidationBoundary>
        </Sentry>
      </Section>
    </Track>
  );
};
